<script setup lang="ts">
import { ref, computed } from 'vue'
import MembersSection from '../school-edit/MembersSection.vue'
import SpecialNotesSection from '../school-edit/SpecialNotesSection.vue'
import BooksSection from '../school-edit/BooksSection.vue'
import MemosSection from '../school-edit/MemosSection.vue'
import AchievementPointHistoriesSection from '../school-edit/AchievementPointHistoriesSection.vue'
import SessionHistoriesSection from '../school-edit/SessionHistoriesSection.vue'

interface RequestPolicy {
  id: number
  key: string
  name: string
}

interface Props {
  postUrl: string
  cancelUrl: string
  isLoggedIn: boolean
  requestPolicies: RequestPolicy[]
}

const props = defineProps<Props>()

const schoolName     = ref('')
const schoolNameKana = ref('')
const manager        = ref('')
const creed          = ref('')
const schoolLevel    = ref<number | null>(null)
const baseBookCount  = ref<number | null>(null)
const maxBookCount   = ref<number | null>(null)
const growthLimit    = ref<number | null>(null)
const schoolSetting  = ref('')
const tagNamesStr    = ref('')

const defaultPolicyId = props.isLoggedIn
  ? (props.requestPolicies.find(p => p.key === 'approval_required')?.id ?? null)
  : (props.requestPolicies.find(p => p.key === 'closed')?.id ?? null)
const memberRequestPolicyId = ref<number | null>(defaultPolicyId)
const growthRequestPolicyId = ref<number | null>(defaultPolicyId)

const visibility   = ref('public')
const editPassword = ref('')
const viewPassword = ref('')

// タブ状態（0-indexed: 0=タグ・基本・特記, 1=構成員・蔵書, 2=設定・メモ, 3=履歴, 4=システム管理）
const activeTab = ref(0)

// prefixes: エラーキーの前方一致で照合（例: "books" は "books.book_type" にもマッチ）
const TABS = [
  { label: 'タグ・基本・特記', prefixes: ['school_name', 'school_name_kana', 'manager', 'creed', 'title', 'special_notes'] },
  { label: '構成員・蔵書',    prefixes: ['members', 'books'] },
  { label: '設定・メモ',     prefixes: ['school_setting', 'memos'] },
  { label: '履歴',           prefixes: ['achievement_point_histories', 'session_histories'] },
  { label: 'システム管理',   prefixes: ['visibility', 'edit_password', 'view_password'] },
]

const membersRef                   = ref<InstanceType<typeof MembersSection>>()
const specialNotesRef              = ref<InstanceType<typeof SpecialNotesSection>>()
const booksRef                     = ref<InstanceType<typeof BooksSection>>()
const memosRef                     = ref<InstanceType<typeof MemosSection>>()
const achievementPointHistoriesRef = ref<InstanceType<typeof AchievementPointHistoriesSection>>()
const sessionHistoriesRef          = ref<InstanceType<typeof SessionHistoriesSection>>()

// 功績点リアルタイム集計（子コンポーネントの expose から算出）
const totalAchievementPoints     = computed(() => achievementPointHistoriesRef.value?.totalGain  ?? 0)
const spentAchievementPoints     = computed(() => achievementPointHistoriesRef.value?.totalSpent ?? 0)
const remainingAchievementPoints = computed(() => totalAchievementPoints.value - spentAchievementPoints.value)

const saving      = ref(false)
const globalError = ref('')
const errors      = ref<Record<string, string[]>>({})

const SCHOOL_SETTING_MAX = 5000

const schoolSettingCount = computed(() => schoolSetting.value.length)

function fieldError(key: string): string {
  return (errors.value[key] ?? []).join(' ')
}

function tabHasError(tabIndex: number): boolean {
  const { prefixes } = TABS[tabIndex]
  return Object.keys(errors.value).some(
    (key) => (errors.value[key] ?? []).length > 0 &&
      prefixes.some((p) => key === p || key.startsWith(`${p}.`))
  )
}

const errorSummary = computed(() => {
  const fieldLabels: Record<string, string> = {
    school_name:   '学派名',
    title:         '学派名',
    school_setting: '学派設定',
    edit_password: '更新用パスワード',
    view_password: '閲覧用パスワード',
  }
  return Object.entries(errors.value)
    .filter(([k, v]) => k !== 'base' && v.length > 0)
    .map(([k, v]) => `${fieldLabels[k] ?? k}: ${v.join(' ')}`)
})

function handleAddToAchievementHistory(session: any) {
  achievementPointHistoriesRef.value?.addFromSession(session)
}

function parsedTagNames(): string[] {
  return tagNamesStr.value.split(',').map((t) => t.trim()).filter((t) => t.length > 0)
}

function scrollToTop() {
  const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'
  window.scrollTo({ top: 0, behavior })
}

async function save() {
  saving.value = true
  globalError.value = ''
  errors.value = {}

  const payload = {
    sheet: {
      visibility:    visibility.value,
      edit_password: editPassword.value,
      view_password: viewPassword.value,
      tag_names:     parsedTagNames().join(','),
    },
    school_sheet: {
      member_request_policy_id: memberRequestPolicyId.value,
      growth_request_policy_id: growthRequestPolicyId.value,
      school_name:      schoolName.value,
      school_name_kana: schoolNameKana.value || null,
      manager:          manager.value,
      creed:            creed.value,
      school_level:     schoolLevel.value,
      base_book_count:  baseBookCount.value,
      max_book_count:   maxBookCount.value,
      growth_limit:     growthLimit.value,
      school_setting:   schoolSetting.value,
      members_attributes:                     membersRef.value?.getData()                   ?? [],
      special_notes_attributes:               specialNotesRef.value?.getData()              ?? [],
      books_attributes:                       booksRef.value?.getData()                     ?? [],
      memos_attributes:                       memosRef.value?.getData()                     ?? [],
      achievement_point_histories_attributes: achievementPointHistoriesRef.value?.getData() ?? [],
      session_histories_attributes:           sessionHistoriesRef.value?.getData()          ?? [],
    },
  }

  const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ''

  try {
    const response = await fetch(props.postUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-CSRF-Token': csrfToken },
      body: JSON.stringify(payload),
    })

    const data = await response.json()

    if (data.ok) {
      window.location.href = data.redirect_to
    } else {
      errors.value = data.errors ?? {}
      globalError.value = (errors.value['base'] ?? []).join(' ') || data.message || '保存に失敗しました'
      // エラーがあるタブへ自動切り替え
      const errorTab = TABS.findIndex((_, i) => tabHasError(i))
      if (errorTab !== -1) activeTab.value = errorTab
      scrollToTop()
    }
  } catch {
    globalError.value = '通信エラーが発生しました'
    scrollToTop()
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <div class="max-w-4xl space-y-4">
    <!-- ヘッダー -->
    <div class="flex items-center justify-between">
      <div>
        <h1 class="text-2xl font-bold text-[#1F2933]">傍流学派シート 新規作成</h1>
        <p class="text-xs text-[#B42318] mt-1">* は必須項目です</p>
      </div>
      <button type="button" @click="save" :disabled="saving"
        class="inline-flex items-center gap-1.5 px-5 py-2 bg-[#0F5E8C] text-white text-sm font-medium rounded hover:bg-[#0B4A70] disabled:opacity-50">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4 shrink-0">
          <path stroke-linecap="round" stroke-linejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
        </svg>
        {{ saving ? '作成中…' : '保存' }}
      </button>
    </div>

    <!-- グローバルエラー -->
    <div v-if="globalError || errorSummary.length > 0" class="p-4 bg-red-50 border border-[#B42318] rounded text-sm text-[#B42318] space-y-1">
      <p v-if="globalError">{{ globalError }}</p>
      <ul v-if="errorSummary.length > 0" class="list-disc pl-4 space-y-0.5">
        <li v-for="msg in errorSummary" :key="msg">{{ msg }}</li>
      </ul>
    </div>

    <!-- タブナビ -->
    <div class="grid grid-cols-5 text-sm text-center border border-[#D8C8A8] rounded overflow-hidden">
      <button
        v-for="(tab, i) in TABS"
        :key="i"
        type="button"
        @click="activeTab = i"
        class="relative px-2 py-3 transition-colors"
        :class="[
          i < TABS.length - 1 ? 'border-r border-[#D8C8A8]' : '',
          activeTab === i
            ? 'bg-white font-medium text-[#1F2933]'
            : 'bg-[#F1E7D0] text-[#667085] hover:bg-[#E8D9C0]'
        ]"
      >
        {{ tab.label }}
        <span v-if="tabHasError(i)" class="absolute top-1.5 right-1.5 w-2 h-2 bg-[#B42318] rounded-full"></span>
      </button>
    </div>

    <!-- タブ1: タグ・基本・特記 -->
    <div v-show="activeTab === 0" class="space-y-4">
      <!-- タグ -->
      <section class="bg-[#FFFCF5] border border-[#D8C8A8] rounded p-5 space-y-3">
        <h2 class="text-sm font-semibold text-[#667085] uppercase tracking-wider">タグ</h2>
        <div>
          <label class="block text-sm font-medium text-[#1F2933]">タグ（カンマ区切り）</label>
          <input v-model="tagNamesStr" type="text" placeholder="マギカロギア, 学派, 魔導書"
            class="mt-1 w-full border border-[#D8C8A8] bg-white rounded px-3 py-2 text-sm focus:outline-none focus:border-[#0F5E8C]" />
          <p class="text-xs text-[#667085] mt-1">カンマで区切って複数のタグを設定できます</p>
        </div>
      </section>

      <!-- 基本情報 -->
      <section class="bg-[#FFFCF5] border border-[#D8C8A8] rounded p-5 space-y-4">
        <h2 class="text-sm font-semibold text-[#667085] uppercase tracking-wider">基本情報</h2>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-[#1F2933]">学派名 <span class="text-[#B42318]">*</span></label>
            <input v-model="schoolName" type="text" placeholder="学派の名前を入力"
              class="mt-1 w-full border border-[#D8C8A8] bg-white rounded px-3 py-2 text-sm focus:outline-none focus:border-[#0F5E8C]" />
            <p v-if="fieldError('title')" class="text-xs text-[#B42318] mt-1">{{ fieldError('title') }}</p>
            <p v-if="fieldError('school_name')" class="text-xs text-[#B42318] mt-1">{{ fieldError('school_name') }}</p>
          </div>
          <div>
            <label class="block text-sm font-medium text-[#1F2933]">ふりがな</label>
            <input v-model="schoolNameKana" type="text" placeholder="がくはのなまえ"
              class="mt-1 w-full border border-[#D8C8A8] bg-white rounded px-3 py-2 text-sm focus:outline-none focus:border-[#0F5E8C]" />
          </div>
          <div>
            <label class="block text-sm font-medium text-[#1F2933]">管理人</label>
            <input v-model="manager" type="text" placeholder="管理人の名前"
              class="mt-1 w-full border border-[#D8C8A8] bg-white rounded px-3 py-2 text-sm focus:outline-none focus:border-[#0F5E8C]" />
          </div>
          <div>
            <label class="block text-sm font-medium text-[#1F2933]">信条</label>
            <input v-model="creed" type="text" placeholder="学派の信条"
              class="mt-1 w-full border border-[#D8C8A8] bg-white rounded px-3 py-2 text-sm focus:outline-none focus:border-[#0F5E8C]" />
          </div>
        </div>
        <div class="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-3 border-t border-[#D8C8A8]">
          <div>
            <label class="block text-sm font-medium text-[#1F2933]">学派レベル</label>
            <input v-model.number="schoolLevel" type="number" min="0"
              class="mt-1 w-full border border-[#D8C8A8] bg-white rounded px-3 py-2 text-sm focus:outline-none focus:border-[#0F5E8C]" />
          </div>
          <div>
            <label class="block text-sm font-medium text-[#1F2933]">基本蔵書数</label>
            <input v-model.number="baseBookCount" type="number" min="0"
              class="mt-1 w-full border border-[#D8C8A8] bg-white rounded px-3 py-2 text-sm focus:outline-none focus:border-[#0F5E8C]" />
          </div>
          <div>
            <label class="block text-sm font-medium text-[#1F2933]">最大蔵書数</label>
            <input v-model.number="maxBookCount" type="number" min="0"
              class="mt-1 w-full border border-[#D8C8A8] bg-white rounded px-3 py-2 text-sm focus:outline-none focus:border-[#0F5E8C]" />
          </div>
          <div>
            <label class="block text-sm font-medium text-[#1F2933]">成長限界</label>
            <input v-model.number="growthLimit" type="number" min="0"
              class="mt-1 w-full border border-[#D8C8A8] bg-white rounded px-3 py-2 text-sm focus:outline-none focus:border-[#0F5E8C]" />
          </div>
        </div>
        <div class="grid grid-cols-3 gap-3 pt-2">
          <div class="rounded p-3 text-center border border-[#D8C8A8] bg-[#EFF8FF]">
            <span class="block text-xs text-[#667085] mb-1">学派功績点 獲得</span>
            <span class="text-lg font-bold text-[#0F5E8C]">{{ totalAchievementPoints }}</span>
            <span class="block text-xs text-[#667085] mt-0.5">自動集計</span>
          </div>
          <div class="rounded p-3 text-center border border-[#FECACA] bg-[#FFF5F5]">
            <span class="block text-xs text-[#667085] mb-1">学派功績点 消費</span>
            <span class="text-lg font-bold text-[#B42318]">{{ spentAchievementPoints }}</span>
            <span class="block text-xs text-[#667085] mt-0.5">自動集計</span>
          </div>
          <div class="rounded p-3 text-center border border-[#D8C8A8] bg-[#ECFDF5]">
            <span class="block text-xs text-[#667085] mb-1">学派功績点 残り</span>
            <span class="text-lg font-bold text-[#027A48]">{{ remainingAchievementPoints }}</span>
            <span class="block text-xs text-[#667085] mt-0.5">自動集計</span>
          </div>
        </div>
      </section>

      <!-- 特記事項 -->
      <SpecialNotesSection ref="specialNotesRef" :initial-items="[]" />
    </div>

    <!-- タブ2: 構成員・蔵書 -->
    <div v-show="activeTab === 1" class="space-y-4">
      <MembersSection ref="membersRef" :initial-items="[]" />
      <BooksSection   ref="booksRef"   :initial-items="[]" />
    </div>

    <!-- タブ3: 設定・メモ -->
    <div v-show="activeTab === 2" class="space-y-4">
      <section class="bg-[#FFFCF5] border border-[#D8C8A8] rounded p-5">
        <h2 class="text-sm font-semibold text-[#667085] uppercase tracking-wider mb-3">学派設定</h2>
        <textarea v-model="schoolSetting" rows="8" placeholder="学派の背景設定や歴史などを記述"
          class="w-full border border-[#D8C8A8] bg-white rounded px-3 py-2 text-sm focus:outline-none focus:border-[#0F5E8C]"
          :class="schoolSettingCount > SCHOOL_SETTING_MAX ? 'border-[#B42318]' : ''"></textarea>
        <p class="text-xs mt-1 text-right" :class="schoolSettingCount > SCHOOL_SETTING_MAX ? 'text-[#B42318]' : 'text-[#667085]'">
          {{ schoolSettingCount }} / {{ SCHOOL_SETTING_MAX }}
        </p>
        <p v-if="fieldError('school_setting')" class="text-xs text-[#B42318] mt-1">{{ fieldError('school_setting') }}</p>
      </section>
      <MemosSection ref="memosRef" :initial-items="[]" />
    </div>

    <!-- タブ4: 履歴 -->
    <div v-show="activeTab === 3" class="space-y-4">
      <AchievementPointHistoriesSection ref="achievementPointHistoriesRef" :initial-items="[]" />
      <SessionHistoriesSection          ref="sessionHistoriesRef"          :initial-items="[]" @add-to-achievement-history="handleAddToAchievementHistory" />
    </div>

    <!-- タブ5: システム管理 -->
    <div v-show="activeTab === 4" class="space-y-4">
      <section class="bg-[#FFFCF5] border border-[#D8C8A8] rounded p-5 space-y-4">
        <h2 class="text-sm font-semibold text-[#667085] uppercase tracking-wider">システム管理</h2>
        <div>
          <label class="block text-sm font-medium text-[#1F2933]">閲覧権限 <span class="text-[#B42318]">*</span></label>
          <select v-model="visibility"
            class="mt-1 border border-[#D8C8A8] bg-white rounded px-3 py-2 text-sm focus:outline-none focus:border-[#0F5E8C]">
            <option value="public">公開（一覧と検索に表示され、URLでも誰でも閲覧できます）</option>
            <option value="limited">リンク公開（一覧と検索には表示されません。URLを知っている人は閲覧できます）</option>
            <option value="private">非公開（一覧と検索には表示されません。ログイン所有者のみ閲覧できます）</option>
          </select>
        </div>
        <div v-if="!isLoggedIn">
          <label class="block text-sm font-medium text-[#1F2933]">
            更新用パスワード<span class="text-[#B42318]"> *</span>
          </label>
          <input v-model="editPassword" type="password"
            class="mt-1 w-full border border-[#D8C8A8] bg-white rounded px-3 py-2 text-sm focus:outline-none focus:border-[#0F5E8C]" />
          <p v-if="fieldError('edit_password')" class="text-xs text-[#B42318] mt-1">{{ fieldError('edit_password') }}</p>
        </div>
        <div v-if="!isLoggedIn && visibility === 'private'">
          <label class="block text-sm font-medium text-[#1F2933]">閲覧用パスワード <span class="text-[#B42318]">*</span></label>
          <input v-model="viewPassword" type="password"
            class="mt-1 w-full border border-[#D8C8A8] bg-white rounded px-3 py-2 text-sm focus:outline-none focus:border-[#0F5E8C]" />
          <p class="text-xs text-[#667085] mt-1">未ログイン作成の非公開シートは閲覧パスワードで閲覧します</p>
          <p v-if="fieldError('view_password')" class="text-xs text-[#B42318] mt-1">{{ fieldError('view_password') }}</p>
        </div>
        <div v-if="isLoggedIn" class="border-t border-[#D8C8A8] pt-4 space-y-3">
          <p class="text-xs font-semibold text-[#667085] uppercase tracking-wider">申請受付設定</p>
          <div>
            <label class="block text-sm font-medium text-[#1F2933]">構成員参加リクエスト</label>
            <p class="text-xs text-[#667085] mt-0.5">「受付中」にすると、他のログインユーザーがこのシートへの構成員参加を申請できるようになります。承認するまでシートには反映されません。</p>
            <select v-model="memberRequestPolicyId"
              class="mt-1 border border-[#D8C8A8] bg-white rounded px-3 py-2 text-sm focus:outline-none focus:border-[#0F5E8C]">
              <option v-for="p in requestPolicies" :key="p.id" :value="p.id">{{ p.name }}</option>
            </select>
          </div>
          <div>
            <label class="block text-sm font-medium text-[#1F2933]">成長報告リクエスト</label>
            <p class="text-xs text-[#667085] mt-0.5">「受付中」にすると、他のログインユーザーがセッション後の功績点・セッション情報を申請できるようになります。承認すると功績点履歴・セッション履歴に追加されます。</p>
            <select v-model="growthRequestPolicyId"
              class="mt-1 border border-[#D8C8A8] bg-white rounded px-3 py-2 text-sm focus:outline-none focus:border-[#0F5E8C]">
              <option v-for="p in requestPolicies" :key="p.id" :value="p.id">{{ p.name }}</option>
            </select>
          </div>
        </div>
      </section>
    </div>

    <!-- 下部ボタン -->
    <div class="flex gap-3">
      <button type="button" @click="save" :disabled="saving"
        class="flex-1 inline-flex items-center justify-center gap-1.5 px-5 py-3 bg-[#0F5E8C] text-white text-sm font-medium rounded hover:bg-[#0B4A70] disabled:opacity-50">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4 shrink-0">
          <path stroke-linecap="round" stroke-linejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
        </svg>
        {{ saving ? '作成中…' : '保存' }}
      </button>
      <a :href="cancelUrl"
        class="flex-1 inline-flex items-center justify-center px-5 py-3 border border-[#D8C8A8] text-[#667085] text-sm font-medium rounded hover:bg-[#F1E7D0]">
        キャンセル
      </a>
    </div>
  </div>
</template>
